import { Component, OnInit } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  UntypedFormControl,
  FormGroupDirective,
  NgForm,
  Validators
} from '@angular/forms';

import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';


import { AuthenticationService } from 'src/app/_services/authentication.service';
import { EmailConfirmDialogComponent } from 'src/app/_modals/email-confirm/email-confirm.component';
import { NotificationService } from 'src/app/_services/notification.service';
import { ConfigService } from 'src/app/_services/config.service';
import _ from "lodash";

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: UntypedFormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

@Component({
  selector: 'app-login',
  templateUrl: 'sign-in.component.html',
  styleUrls: ['sign-in.component.css']
})
export class SignInComponent implements OnInit {
  isLoading = true;
  loginIsDisabled = false;
  isLoadingNotifications = true;
  frmUsername = new UntypedFormControl('', [Validators.required]);
  frmPassword = new UntypedFormControl('', [Validators.required]);
  frmResetUsername = new UntypedFormControl('', [Validators.required]);
  passwordInputType = 'password';
  notificationList = null;

  matcher = new MyErrorStateMatcher();

  constructor(
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    public snackBar: MatSnackBar,
    private configService: ConfigService,
  ) {}

  ngOnInit(): void {
    this.authenticationService.clearToken();
    this.loadNotifications();
    this.isLoading = false;
  }

  isEmailValid(emailAddress): boolean {
    const re = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return re.test(String(emailAddress).toLowerCase());
  }

  getConfigValues(key): string {
    try {
      return this.configService.get(key);
    } catch (e) {
      return key;
    }
  }

  doFlyOver({ user, navigateUrl, isFlyOverUrl = false, jsonWebToken }) {
    if (user.passwordExpired) {
      window.location.href = navigateUrl;
      return;
    }

    if (isFlyOverUrl) {
      navigateUrl = `${navigateUrl}/${jsonWebToken}`;
    }

    window.top.location.href = navigateUrl;
  }

  // For front end tests
  shouldDisableLogin(): boolean {
    const bothValid = this.frmUsername.valid && this.frmPassword.valid;
    return !bothValid;
  }

  shouldDisableReset(): boolean {
    return !this.frmResetUsername.valid;
  }

  shouldShowLoader(): boolean {
    return this.isLoading || this.isLoadingNotifications;
  }

  shouldShowNotifications(): boolean {
    return this.notificationList && this.notificationList.length > 0;
  }

  // Click events
  onClickPasswordIcon(): void {
    switch (this.passwordInputType) {
      case 'password':
        this.passwordInputType = 'text';
        break;
      case 'text':
        this.passwordInputType = 'password';
        break;
      default:
        this.passwordInputType = 'password';
        break;
    }
  }

  onClickSumbitLogin(): void {
    this.isLoading = true;
    this.authenticationService
      .login(this.frmUsername.value.trim(), this.frmPassword.value.trim())
      .toPromise()
      .then(data => {
        this.isLoading = false;

        const { user } = data;
        if (!this.isEmailValid(user.email)) {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.data = {
            id: 1,
            title: 'Confirm email'
          };
          return this.dialog
            .open(EmailConfirmDialogComponent, dialogConfig)
            .afterClosed()
            .toPromise()
            .then(() => data);
        }
        return data;
      })
      .then(this.doFlyOver, error => {
        this.isLoading = false;

        let message =
          'The service is currently unavailable, please try again in a few minutes.';
        if (typeof error === 'string') {
          message = error;
        }
        if (error.errorMessage) {
          message = error.errorMessage;
        }
        this.snackBar.open(message, 'Dismiss', {
          duration: 8000
        });
      });
  }

  onClickSubmitReset(): void {
    this.isLoading = true;
    this.authenticationService
      .requestPasswordReset(this.frmResetUsername.value)
      .toPromise()
      .then(
        ({ messageDetail }) => {
          this.isLoading = false;
          if (!messageDetail) {
            return;
          }

          return this.snackBar.open(
            messageDetail,
            'Dismiss',
            {
              duration: 30000
            }
          );
        },
        error => {
          this.isLoading = false;
          return this.snackBar.open(
            error.errorMessage || `Something went wrong while trying to send the reset email.`,
            'Dismiss',
            {
              duration: 8000
            }
          );
        }
      );
  }

  private loadNotifications() {
    return this.notificationService
      .getNotifications()
      .toPromise()
      .then(
        notificationList => {
          this.notificationList = notificationList;
          _.forEach(notificationList, notification => {
            // console.log('Debug Dale notification', notification, notification.blockLogin);
            if (notification && notification.blockLogin) {
              this.loginIsDisabled = notification.blockLogin;
            }
          });
          this.isLoadingNotifications = false;
        },
        error => {
          return this.snackBar.open(
            'Could not retrieve the notification data, please try again',
            'Dismiss',
            {
              duration: 8000
            }
          );
        }
      );
  }
}
